import { RebuySDK } from '@rebuy/core-sdk'; // https://www.npmjs.com/package/@rebuy/core-sdk

class RebuyRecommendations extends HTMLElement {
  constructor() {
    super();
    this.sdk = null;
    this.recommendationType = this.getAttribute('data-recommendation-type') || 'top_sellers';
    this.limit = this.getAttribute('data-limit') || 8;
    this.productId = this.getAttribute('data-product-id') || null;
    this.staticId = this.getAttribute('data-static-id') || null;
    this.size = this.getAttribute('data-size') || 'large';
    this.emptyMessage = this.getAttribute('data-empty-message') || 'No products to display.';
    this.container = this.querySelector('horizontal-carousel > div');
  }

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  async initialize() {
    this.sdk = new RebuySDK({
      apiKey: window.umbrellaConfig.rebuy.key,
    });

    // Wait for the session to load the stored UUID from localStorage
    if (this.sdk.session.initPromise) {
      await this.sdk.session.initPromise;
    }

    // Track product view
    if (this.productId) {
      await this.sdk.tracking.productView(this.productId);
    }

    try {
      const products = await this.getProducts();
      if (products?.length) {
        await this.renderProducts(products);
      } else {
        this.renderEmpty();
      }
    } catch (error) {
      console.error('[RebuyRecommendations] Failed to load products:', error);
    }
  }

  cleanup() {
    this.sdk = null;
  }

  getProducts() {
    switch (this.recommendationType) {
      case 'static_recommendations':
        return this.sdk.products.fetchFromDataSource(this.staticId, { limit: this.limit, shopify_product_ids: [this.productId] });
      case 'top_sellers':
        return this.sdk.products.getTopSellers({ limit: this.limit});
      case 'similar_products':
        return this.sdk.products.getSimilar({ limit: this.limit, shopify_product_ids: [this.productId] });
      case 'recently_viewed':
        return this.sdk.products.getRecentlyViewed({ limit: this.limit });
      case 'previously_purchased':
        return this.sdk.products.getPurchased({ limit: this.limit });
    }
  }

  /**
   * Fetches server-rendered product card HTML for each product
   * using Shopify's Section Rendering API, then injects them into the DOM.
   */
  async renderProducts(products) {
    const sectionId = this.size == 'compact' ? 'rebuy-product-card-compact' : 'rebuy-product-card';
    const cardClass = `flex-grow-0 flex-shrink-0 ${this.size == 'narrow' ? 'w-[170px] mr-3' : 'w-[170px] md:w-[325px] mr-[0.9375rem] md:mr-[1.875rem]'}`;

    const cardPromises = products.map(async (product) => {
      if (!product.handle) return null;

      try {
        const response = await fetch(`/products/${product.handle}?section_id=${sectionId}`);
        if (!response.ok) return null;
        return response.text();
      } catch {
        return null;
      }
    });

    const cards = await Promise.all(cardPromises);

    this.container.innerHTML = cards
      .filter(Boolean)
      .map((html) => this.size == 'compact' ? html : `<div class="${cardClass}">${html}</div>`)
      .join('');
  }

  /**
   * Replaces the container content with an empty state message.
   */
  renderEmpty() {
    this.container.innerHTML = `<p class="text-center p-4 h-20 w-full">${this.emptyMessage}</p>`;
  }
}

customElements.define('rebuy-recommendations', RebuyRecommendations);